import { DataProxy, gql } from '@apollo/client';
import {
  Restaurant_EmployeesQuery,
  Restaurant_EmployeesQueryVariables,
  RestaurantEmployeeFragment,
} from '@/types';
import {
  useAuthenticatedUserContext,
  useUserContext,
} from '@/contexts/user-context';
import { useCallback, useMemo } from 'react';
import { useWrappedQuery } from './use-wrapped-query';

export const RESTAURANT_EMPLOYEES_QUERY = gql`
  fragment RestaurantEmployee on GetRestaurantEmployeesResponse {
    chat_employee_id
    employee_contact_email
    employee_contact_phone_number
    employee_contact_protected
    employee_id
    employee_login_email
    employee_name
    employee_status
    restaurant_employee_id
    restaurant_employee_role
    show_app_payments_tab
  }

  query Restaurant_Employees(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $where: businesses_employees_bool_exp
  ) {
    getRestaurantEmployees(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      ...RestaurantEmployee
    }
  }
`;

type UpdateCacheFn = (
  cachedRestaurantEmployees: RestaurantEmployeeFragment[],
) => RestaurantEmployeeFragment[];

export const useRestaurantEmployees = (
  restaurantUUID?: string,
  supplierUUID?: string,
) => {
  const { user, selectedRestaurantRelationship, selectedRestaurantUUID } =
    useAuthenticatedUserContext();

  const queryResult = useWrappedQuery<
    Restaurant_EmployeesQuery,
    Restaurant_EmployeesQueryVariables
  >(RESTAURANT_EMPLOYEES_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      restaurantUUID: restaurantUUID || selectedRestaurantUUID,
      supplierUUID: supplierUUID || selectedRestaurantRelationship.supplierUUID,
    },
  });

  return useMemo(() => {
    return {
      restaurantEmployees: (
        queryResult.data?.getRestaurantEmployees || []
      ).filter((re) => re.employee_id !== user.uuid),
      ...queryResult,
    };
  }, [queryResult, user.uuid]);
};

export const useUpdateCachedRestaurantEmployee = () => {
  const { selectedRestaurantRelationship } = useUserContext();

  return useCallback(
    (client: DataProxy, updateCacheFn: UpdateCacheFn) => {
      try {
        const variables = {
          restaurantUUID: selectedRestaurantRelationship.uuid,
          supplierUUID: selectedRestaurantRelationship.supplierUUID,
        };

        const cachedQuery = client.readQuery<
          Restaurant_EmployeesQuery,
          Restaurant_EmployeesQueryVariables
        >({
          query: RESTAURANT_EMPLOYEES_QUERY,
          variables,
        });

        if (cachedQuery) {
          client.writeQuery<Restaurant_EmployeesQuery>({
            data: {
              ...cachedQuery,
              getRestaurantEmployees: updateCacheFn(
                cachedQuery.getRestaurantEmployees,
              ),
            },
            query: RESTAURANT_EMPLOYEES_QUERY,
            variables,
          });
        }
      } catch {
        // Ignore cache update errors
      }
    },
    [selectedRestaurantRelationship],
  );
};
